import type { CampaignUpsertInput } from '@/lib/campaigns'
import { CampaignImageUploadField } from '@/components/CampaignImageUploadField'
import { CampaignRichTextField } from '@/components/CampaignRichTextField'
import {
  TEMPLATE3_ADVANTAGES_COUNT,
  TEMPLATE3_FAQ_COUNT,
  normalizeTemplate3Advantages,
  normalizeTemplate3Faq,
  listMissingTemplate3Fields,
  type Template3MissingFieldKey,
} from '@/lib/template3-campaign'
import styles from './CampanhaEditPage.module.css'
import { TemplateContentSection } from './editor/TemplateContentSection'

const ADVANTAGE_IMAGE_FIELDS = [
  'template3_advantage_image_1',
  'template3_advantage_image_2',
  'template3_advantage_image_3',
] as const

const ADVANTAGE_IMAGE_PLACEMENT = [
  'Foto à direita do texto no computador. No celular fica acima do texto.',
  'Foto à esquerda do texto no computador. No celular fica acima do texto.',
  'Foto à direita do texto no computador. No celular fica acima do texto.',
] as const

type Template3CampaignFieldsProps = {
  form: CampaignUpsertInput
  onChange: (patch: Partial<CampaignUpsertInput>) => void
  onFileError: (message: string | null) => void
  imageAccept: string
  validateFileSize: (file: File, maxBytes: number, label: string) => string | null
  maxImageBytes: number
  /** Quando definido, renderiza só essas seções (modal de campos faltantes). */
  visibleSections?: Template3MissingFieldKey[]
  /** Prefixo de ids para evitar colisão quando o modal e a aba Mídia estão montados. */
  idPrefix?: string
  hideIntro?: boolean
}

function shouldShowSection(
  visibleSections: Template3MissingFieldKey[] | undefined,
  key: Template3MissingFieldKey
): boolean {
  return visibleSections === undefined || visibleSections.includes(key)
}

function shouldShowFaqGroup(
  visibleSections: Template3MissingFieldKey[] | undefined
): boolean {
  if (visibleSections === undefined) {
    return true
  }
  return visibleSections.some(
    (key) => key === 'faq-1' || key === 'faq-2' || key === 'faq-3'
  )
}

export function Template3CampaignFields({
  form,
  onChange,
  onFileError,
  imageAccept,
  validateFileSize,
  maxImageBytes,
  visibleSections,
  idPrefix = 'template3',
  hideIntro = false,
}: Template3CampaignFieldsProps) {
  const advantages = normalizeTemplate3Advantages(form.template3_advantages)
  const faq = normalizeTemplate3Faq(form.template3_faq)
  const missing = new Set(listMissingTemplate3Fields(form).map((field) => field.key))
  const pending = (keys: Template3MissingFieldKey[]) =>
    keys.some((key) => missing.has(key))
  const groups: Template3MissingFieldKey[][] = [
    ['eyebrow', 'heroBackground'],
    ['aboutTitle', 'aboutText', 'aboutImage'],
    ['advantage-1', 'advantage-2', 'advantage-3'],
    ['faq-1', 'faq-2', 'faq-3'],
    ['floatingCta'],
  ]
  const completed = groups.filter((keys) => !pending(keys)).length

  const showEyebrow = shouldShowSection(visibleSections, 'eyebrow')
  const showHeroBackground = shouldShowSection(visibleSections, 'heroBackground')
  const showAboutImage = shouldShowSection(visibleSections, 'aboutImage')
  const showFloatingCta = shouldShowSection(visibleSections, 'floatingCta')
  const showAboutTitle = shouldShowSection(visibleSections, 'aboutTitle')
  const showAboutText = shouldShowSection(visibleSections, 'aboutText')
  const visibleAdvantages = advantages
    .map((advantage, index) => ({ advantage, index }))
    .filter(({ index }) =>
      shouldShowSection(
        visibleSections,
        `advantage-${index + 1}` as Template3MissingFieldKey
      )
    )
  const showFaq = shouldShowFaqGroup(visibleSections)
  const showHeroBlock = showHeroBackground || showEyebrow
  const showAboutBlock = showAboutTitle || showAboutText || showAboutImage
  const showHowItWorksNote = visibleSections === undefined

  return (
    <div>
      {!hideIntro ? (
        <>
          <h3 className={styles.editSubsectionTitle}>Textos e imagens da página</h3>
          <p className={styles.editSubsectionLead}>
            Todos os campos das seções editáveis são obrigatórios neste modelo. Preencha
            na ordem da página e recolha as seções que já revisou.
          </p>
          <div className={styles.templateProgress}>
            <span>
              <strong>{completed} de 5</strong> seções preenchidas
            </span>
            <progress
              value={completed}
              max={5}
              aria-label="Seções obrigatórias preenchidas"
            />
          </div>
        </>
      ) : null}

      {showHeroBlock ? (
        <TemplateContentSection
          title="1. Topo da página"
          layout={showHeroBackground && showEyebrow ? 'paired' : 'stack'}
          pending={pending(['eyebrow', 'heroBackground'])}
        >
          <p className={styles.editSubsectionLead}>
            Primeira faixa que o visitante vê: fundo, frase colorida, título e a foto do
            prêmio. Edite o título e a descrição em Conteúdo. A foto do prêmio fica em
            Imagens e cores, no campo Imagem principal da campanha.
          </p>
          {showHeroBackground ? (
            <CampaignImageUploadField
              required
              label="Foto de fundo do topo"
              hint="Fica atrás do título, da frase colorida e da foto do prêmio. Exemplo: imagem ampla do prêmio ou da causa."
              emptyHint="PNG, JPG, WEBP ou GIF, até 5 MB. Tamanho sugerido: 2000 x 1333 px"
              accept={imageAccept}
              maxBytes={maxImageBytes}
              validateFileSize={validateFileSize}
              file={form.template3_hero_background ?? null}
              imageUrl={form.template3_hero_background_url ?? null}
              onFileChange={(nextFile) =>
                onChange({ template3_hero_background: nextFile })
              }
              onFileError={onFileError}
              previewAlt="Prévia da foto de fundo do topo da página"
              inputId={`${idPrefix}-hero-background`}
              validationLabel="A foto de fundo do topo da página"
            />
          ) : null}
          {showEyebrow ? (
            <div className={styles.editField}>
              <label className={styles.editLabel} htmlFor={`${idPrefix}-eyebrow`}>
                Frase pequena acima do título
                <span className={styles.editLabelHint}>
                  Texto na cor do botão, logo acima do nome da campanha. Exemplo: Título
                  do Sorteio.
                </span>
              </label>
              <CampaignRichTextField
                aria-required
                id={`${idPrefix}-eyebrow`}
                variant="inline"
                placeholder="Ex.: Título do Sorteio"
                aria-label="Frase pequena acima do título"
                value={form.template3_eyebrow ?? ''}
                onChange={(html) => onChange({ template3_eyebrow: html })}
              />
            </div>
          ) : null}
        </TemplateContentSection>
      ) : null}

      {showAboutBlock ? (
        <TemplateContentSection
          title="2. Sobre o sorteio"
          layout={showAboutTitle && showAboutImage && showAboutText ? 'about' : 'stack'}
          pending={pending(['aboutTitle', 'aboutText', 'aboutImage'])}
        >
          <p className={styles.editSubsectionLead}>
            Seção depois da compra de números. No celular aparece título, foto e texto. No
            computador o título e o texto ficam à esquerda e a foto à direita.
          </p>
          {showAboutTitle ? (
            <div className={styles.editField}>
              <label className={styles.editLabel} htmlFor={`${idPrefix}-about-title`}>
                Título da seção Sobre
                <span className={styles.editLabelHint}>
                  Aparece em destaque no início da seção. Exemplo: Sobre o Sorteio.
                </span>
              </label>
              <CampaignRichTextField
                aria-required
                id={`${idPrefix}-about-title`}
                variant="inline"
                placeholder="Ex.: Sobre o Sorteio"
                aria-label="Título da seção Sobre"
                value={form.template3_about_title ?? ''}
                onChange={(html) => onChange({ template3_about_title: html })}
              />
            </div>
          ) : null}
          {showAboutImage ? (
            <CampaignImageUploadField
              required
              label="Foto da seção Sobre"
              hint="No computador fica à direita do texto. No celular aparece logo abaixo do título."
              emptyHint="PNG, JPG, WEBP ou GIF, até 5 MB. Tamanho sugerido: 650 x 480 px"
              accept={imageAccept}
              maxBytes={maxImageBytes}
              validateFileSize={validateFileSize}
              file={form.template3_about_image ?? null}
              imageUrl={form.template3_about_image_url ?? null}
              onFileChange={(nextFile) => onChange({ template3_about_image: nextFile })}
              onFileError={onFileError}
              previewAlt="Prévia da foto da seção Sobre"
              inputId={`${idPrefix}-about-image`}
              validationLabel="A foto da seção Sobre"
            />
          ) : null}
          {showAboutText ? (
            <div className={styles.editField}>
              <label className={styles.editLabel} htmlFor={`${idPrefix}-about-text`}>
                Texto da seção Sobre
                <span className={styles.editLabelHint}>
                  Parágrafo abaixo da foto no celular e ao lado dela no computador. Conte
                  a história da campanha e para quem vai a compra.
                </span>
              </label>
              <CampaignRichTextField
                aria-required
                id={`${idPrefix}-about-text`}
                variant="block"
                placeholder="Ex.: As compras nesta campanha ajudam..."
                aria-label="Texto da seção Sobre"
                value={form.template3_about_text ?? ''}
                onChange={(html) => onChange({ template3_about_text: html })}
                minHeight={140}
              />
            </div>
          ) : null}
        </TemplateContentSection>
      ) : null}

      {showHowItWorksNote ? (
        <TemplateContentSection title="3. Como funciona o sorteio">
          <p className={styles.editSubsectionLead}>
            Esta faixa é padrão do Template 3 e não precisa ser preenchida: Escolha os
            números, Faça a compra, Aguarde o sorteio e Confira o resultado.
          </p>
        </TemplateContentSection>
      ) : null}

      {visibleAdvantages.length > 0 ? (
        <TemplateContentSection
          title={`4. Por que participar (${TEMPLATE3_ADVANTAGES_COUNT} blocos)`}
          pending={pending(['advantage-1', 'advantage-2', 'advantage-3'])}
        >
          <p className={styles.editSubsectionLead}>
            Três blocos depois da contagem regressiva, na ordem em que aparecem na página.
          </p>
          {visibleAdvantages.map(({ advantage, index }) => {
            const field = ADVANTAGE_IMAGE_FIELDS[index]
            const placement = ADVANTAGE_IMAGE_PLACEMENT[index]
            return (
              <div
                className={styles.templateContentPair}
                key={`${idPrefix}-advantage-${index}`}
              >
                {field ? (
                  <CampaignImageUploadField
                    required
                    label={`Foto do bloco ${index + 1}`}
                    hint={placement}
                    emptyHint="PNG, JPG, WEBP ou GIF, até 5 MB. Tamanho sugerido: 660 x 394 px"
                    accept={imageAccept}
                    maxBytes={maxImageBytes}
                    validateFileSize={validateFileSize}
                    file={form[field] ?? null}
                    imageUrl={advantage.image_url}
                    onFileChange={(nextFile) => onChange({ [field]: nextFile })}
                    onFileError={onFileError}
                    previewAlt={`Prévia da foto do bloco ${index + 1}`}
                    inputId={`${idPrefix}-advantage-image-${index}`}
                    validationLabel={`A foto do bloco ${index + 1} de Por que participar`}
                  />
                ) : null}
                <div className={styles.editField}>
                  <label
                    className={styles.editLabel}
                    htmlFor={`${idPrefix}-advantage-title-${index}`}
                  >
                    Título do bloco {index + 1}
                    <span className={styles.editLabelHint}>
                      Faixa escura no bloco. Exemplo: Ajude uma causa.
                    </span>
                  </label>
                  <CampaignRichTextField
                    aria-required
                    id={`${idPrefix}-advantage-title-${index}`}
                    variant="inline"
                    placeholder="Ex.: Ajude uma causa"
                    aria-label={`Título do bloco ${index + 1}`}
                    value={advantage.title}
                    onChange={(html) => {
                      const next = [...advantages]
                      next[index] = { ...next[index], title: html }
                      onChange({ template3_advantages: next })
                    }}
                  />
                </div>
                <div className={styles.editField}>
                  <label
                    className={styles.editLabel}
                    htmlFor={`${idPrefix}-advantage-text-${index}`}
                  >
                    Texto do bloco {index + 1}
                    <span className={styles.editLabelHint}>
                      Descrição abaixo do título, ao lado da foto.
                    </span>
                  </label>
                  <CampaignRichTextField
                    aria-required
                    id={`${idPrefix}-advantage-text-${index}`}
                    variant="block"
                    placeholder="Ex.: Sua compra ajuda diretamente quem precisa."
                    aria-label={`Texto do bloco ${index + 1}`}
                    value={advantage.description}
                    onChange={(html) => {
                      const next = [...advantages]
                      next[index] = { ...next[index], description: html }
                      onChange({ template3_advantages: next })
                    }}
                    minHeight={96}
                  />
                </div>
              </div>
            )
          })}
        </TemplateContentSection>
      ) : null}

      {showFaq ? (
        <TemplateContentSection
          title={`5. Dúvidas frequentes (${TEMPLATE3_FAQ_COUNT})`}
          pending={pending(['faq-1', 'faq-2', 'faq-3'])}
        >
          <p className={styles.editSubsectionLead}>
            Lista no final da página, depois da faixa de regulamento. O visitante toca na
            pergunta para ler a resposta.
          </p>
          {faq.map((item, index) => (
            <div className={styles.editGrid2} key={`${idPrefix}-faq-${index}`}>
              <div className={styles.editField}>
                <label
                  className={styles.editLabel}
                  htmlFor={`${idPrefix}-faq-question-${index}`}
                >
                  Pergunta {index + 1}
                  <span className={styles.editLabelHint}>
                    Título da linha na lista. Exemplo: Como participo do sorteio.
                  </span>
                </label>
                <CampaignRichTextField
                  aria-required
                  id={`${idPrefix}-faq-question-${index}`}
                  variant="inline"
                  placeholder="Ex.: Como participo do sorteio"
                  aria-label={`Pergunta ${index + 1}`}
                  value={item.question}
                  onChange={(html) => {
                    const next = [...faq]
                    next[index] = { ...next[index], question: html }
                    onChange({ template3_faq: next })
                  }}
                />
              </div>
              <div className={styles.editField}>
                <label
                  className={styles.editLabel}
                  htmlFor={`${idPrefix}-faq-answer-${index}`}
                >
                  Resposta {index + 1}
                  <span className={styles.editLabelHint}>
                    Texto que abre ao tocar na pergunta.
                  </span>
                </label>
                <CampaignRichTextField
                  aria-required
                  id={`${idPrefix}-faq-answer-${index}`}
                  variant="block"
                  placeholder="Ex.: Escolha os números e conclua a compra."
                  aria-label={`Resposta ${index + 1}`}
                  value={item.answer}
                  onChange={(html) => {
                    const next = [...faq]
                    next[index] = { ...next[index], answer: html }
                    onChange({ template3_faq: next })
                  }}
                  minHeight={96}
                />
              </div>
            </div>
          ))}
        </TemplateContentSection>
      ) : null}

      {showFloatingCta ? (
        <TemplateContentSection
          title="6. Botão flutuante de compra"
          pending={pending(['floatingCta'])}
        >
          <p className={styles.editSubsectionLead}>
            Imagem fixa no canto inferior direito da página pública. Ao tocar, o visitante
            vai até a compra de números. Não use texto no painel: a própria imagem é o
            botão.
          </p>
          <CampaignImageUploadField
            required
            label="Imagem do botão flutuante"
            hint="Fica no canto inferior direito, por cima da página. Use fundo transparente se quiser só o desenho do botão."
            emptyHint="PNG, JPG, WEBP ou GIF, até 5 MB. Tamanho sugerido: 240 x 80 px"
            accept={imageAccept}
            maxBytes={maxImageBytes}
            validateFileSize={validateFileSize}
            file={form.template3_floating_cta_image ?? null}
            imageUrl={form.template3_floating_cta_image_url ?? null}
            onFileChange={(nextFile) =>
              onChange({ template3_floating_cta_image: nextFile })
            }
            onFileError={onFileError}
            previewAlt="Prévia da imagem do botão flutuante de compra"
            inputId={`${idPrefix}-floating-cta`}
            validationLabel="A imagem do botão flutuante de compra"
          />
        </TemplateContentSection>
      ) : null}
    </div>
  )
}
